import { useEffect, useState, type CSSProperties } from "react";
import { useGame } from "@game/game-context";
import type { Element } from "@game/types";
import type { ApiError } from "@game/services";

interface StarterEntry {
  speciesId: string;
  displayName: string;
  element: Element;
  description: string;
}

const ELEMENT_COLORS: Record<Element, string> = {
  neutral: "#9ca3af",
  fire: "#f87171",
  water: "#60a5fa",
  earth: "#a3a07a",
  wind: "#86efac",
  thunder: "#facc15",
  ice: "#7dd3fc",
  holy: "#fde68a",
  dark: "#a78bfa",
  metal: "#94a3b8",
  wood: "#65a30d",
};

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "linear-gradient(135deg, #0b0d12 0%, #1a1538 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 5000,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  color: "#e6e8ee",
};

const cardStyle = (selected: boolean, color: string): CSSProperties => ({
  width: 200,
  background: "rgba(255,255,255,0.04)",
  border: `2px solid ${selected ? color : "rgba(255,255,255,0.18)"}`,
  borderRadius: 12,
  padding: 16,
  cursor: "pointer",
  transition: "border-color 120ms ease, transform 120ms ease",
  transform: selected ? "translateY(-4px)" : "none",
});

export function OnboardingScene() {
  const game = useGame();
  const [starters, setStarters] = useState<StarterEntry[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await game.client.api.get<{ starters: StarterEntry[] }>("/onboarding/starters", { noAuth: true });
        setStarters(r.starters);
      } catch (err) {
        setError("Could not load starter options.");
        void err;
      }
    })();
  }, [game]);

  const confirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await game.client.api.post("/onboarding/pick-starter", { starterSpeciesId: selected });
      // Onboarding done — reboot loads world.
      game.setStage({ kind: "boot" });
    } catch (err) {
      const code = (err as ApiError).code ?? "unknown";
      setError(code === "already_completed" ? "You've already chosen a starter." : `Failed (${code}).`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={{ width: "min(900px, 90vw)" }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, textAlign: "center", margin: "0 0 8px" }}>
          Choose your partner
        </h2>
        <div style={{ textAlign: "center", opacity: 0.65, marginBottom: 28, fontSize: 14 }}>
          You can't undo this — pick the partner that calls to you.
        </div>

        {!starters && !error && (
          <div style={{ textAlign: "center", opacity: 0.65 }}>Loading starters…</div>
        )}
        {error && (
          <div style={{ textAlign: "center", color: "#ef4444" }}>{error}</div>
        )}

        {starters && (
          <div style={{ display: "flex", justifyContent: "center", gap: 18, flexWrap: "wrap" }}>
            {starters.map((s) => {
              const color = ELEMENT_COLORS[s.element];
              return (
                <button
                  key={s.speciesId}
                  type="button"
                  onClick={() => setSelected(s.speciesId)}
                  style={cardStyle(selected === s.speciesId, color)}
                >
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      margin: "0 auto",
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${color}55, transparent 70%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 48,
                    }}
                  >
                    {/* Placeholder — replace with sprite once art ships. */}
                    🥚
                  </div>
                  <div style={{ marginTop: 12, fontWeight: 600, fontSize: 16, textAlign: "center" }}>
                    {s.displayName}
                  </div>
                  <div style={{ marginTop: 4, textAlign: "center", color, fontSize: 11, textTransform: "uppercase" }}>
                    {s.element}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8, textAlign: "center", minHeight: 50 }}>
                    {s.description}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {starters && (
          <div style={{ marginTop: 28, textAlign: "center" }}>
            <button
              type="button"
              onClick={confirm}
              disabled={!selected || submitting}
              style={{
                background: selected ? "linear-gradient(135deg, #4cb1ff, #a855f7)" : "rgba(255,255,255,0.08)",
                color: "#fff",
                border: "none",
                padding: "10px 32px",
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 600,
                cursor: selected ? "pointer" : "not-allowed",
              }}
            >
              {submitting ? "Hatching…" : "Confirm"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
